/**
 * Fallback data and small helpers of the audio screens (/audio).
 *
 * The real lists - models, voices, languages and limits - are sent by the server (GET /api/ai/settings/audio),
 * so a model that the server adds needs no change here. The lists below are used only until that answer arrives,
 * or when it fails (for example: a server that is older than this screen).
 */

export const PROVIDER_GEMINI_API = 'gemini-api'; // Gemini 3.8: Gemini API, the answer is a WAV file
export const PROVIDER_INWORLD = 'inworld'; // Inworld TTS: the answer is an MP3 file

/** The same models as the server (4_services/ai/text_to_speech/settings.js) */
export const FALLBACK_MODELS = [
    { id: 'gemini-3.8-flash-tts', provider: PROVIDER_GEMINI_API, label: 'Gemini 3.8 Flash TTS', preview: false },
    { id: 'gemini-3.8-flash-lite-tts', provider: PROVIDER_GEMINI_API, label: 'Gemini 3.8 Flash-Lite TTS', preview: false },
];

/** The same models as the server (4_services/ai/text_to_speech/inworld/inworld_settings.js) */
export const FALLBACK_INWORLD_MODELS = [
    { id: 'inworld-tts-2', provider: PROVIDER_INWORLD, label: 'Inworld TTS 2', supportsDeliveryMode: true, priceUsdPerMillionChars: 25 },
    { id: 'inworld-tts-2-flash', provider: PROVIDER_INWORLD, label: 'Inworld TTS 2 Flash', supportsDeliveryMode: false, priceUsdPerMillionChars: 15 },
];

export const INWORLD_DELIVERY_MODES = ['STABLE', 'BALANCED', 'CREATIVE'];
export const INWORLD_NIQQUD_MODES = ['keep', 'strip'];
export const INWORLD_SPEAKING_RATE = { min: 0.5, max: 1.5, default: 1 };
export const INWORLD_MAX_TEXT_CHARS = 2000;
export const INWORLD_MAX_VOICES_PER_CELL = 8;
export const INWORLD_VOICE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
/** Only for the estimate of the cost on the settings screen: a word or a short phrase is about this many characters */
export const AVERAGE_CHARS_PER_FILE = 8;

/** What to do when the server has no Inworld key (the same words on the screens of the rules and of the Inworld settings) */
export const INWORLD_KEY_NOTE = 'כדי להשתמש ב-Inworld צריך להוסיף לשרת את הסוד lexillon/prod-inworld ב-AWS Secrets Manager '
    + '(האזור il-central-1), עם שדה אחד בשם key שבו המפתח (הערך Basic Base64 מ-Inworld). '
    + 'אין צורך להפעיל את השרת מחדש: אחרי ההוספה לוחצים כאן על "טעינה מחדש מהשרת".';

/** The default voices of Inworld when the server did not send its own (the same as the server) */
export const FALLBACK_INWORLD_VOICES = {
    he: { male: ['Oren'], female: ['Yael'] },
    en_US: { male: ['Cedric', 'Dennis'], female: ['Ashley', 'Jessica'] },
    en_GB: { male: ['Craig'], female: ['Eleanor'] },
    ar: { male: ['Omar'], female: ['Nour'] },
};

/** The providers of the automatic creation: the ids that the server saves in the rules */
export const RULE_PROVIDER_GOOGLE = 'google';
export const RULE_PROVIDER_INWORLD = 'inworld';
export const FALLBACK_AUTO_PROVIDERS = [
    { id: RULE_PROVIDER_GOOGLE, label: 'Google (Gemini)' },
    { id: RULE_PROVIDER_INWORLD, label: 'Inworld' },
];

/** The dictionaries of the rules (the keys are the ones of the server) and the field of an entry that is spoken */
export const DICTIONARY_INFO = {
    heb_to_eng: { label: 'מילון עברי-אנגלי' },
    eng_to_heb: { label: 'מילון אנגלי-עברי' },
};
export const FIELD_INFO = {
    original: 'מילת המקור',
    trns: 'התרגום',
    trnsAr: 'התרגום לערבית',
};

/** How the model of the test form is written: the provider that it belongs to */
export const providerOfModel = (model) => (model?.provider === PROVIDER_INWORLD ? PROVIDER_INWORLD : PROVIDER_GEMINI_API);

/**
 * The 30 prebuilt voices of Gemini - the same names work in every model and every language.
 * Gender: Google's list (Cloud Text-to-Speech, Gemini-TTS). Description: as in the previous version of this screen.
 */
export const VOICE_INFO = {
    Achernar: { gender: 'female', description: 'Soft and gentle' },
    Achird: { gender: 'male', description: 'Friendly and approachable' },
    Algenib: { gender: 'male', description: 'Gravelly texture' },
    Algieba: { gender: 'male', description: 'Smooth and pleasant' },
    Alnilam: { gender: 'male', description: 'Firm and strong' },
    Aoede: { gender: 'female', description: 'Breezy and natural' },
    Autonoe: { gender: 'female', description: 'Bright and optimistic' },
    Callirrhoe: { gender: 'female', description: 'Easy-going and relaxed' },
    Charon: { gender: 'male', description: 'Informative and clear' },
    Despina: { gender: 'female', description: 'Smooth and flowing' },
    Enceladus: { gender: 'male', description: 'Breathy and soft' },
    Erinome: { gender: 'female', description: 'Clear and precise' },
    Fenrir: { gender: 'male', description: 'Excitable and dynamic' },
    Gacrux: { gender: 'female', description: 'Mature and experienced' },
    Iapetus: { gender: 'male', description: 'Clear and articulate' },
    Kore: { gender: 'female', description: 'Firm and confident' },
    Laomedeia: { gender: 'female', description: 'Upbeat and lively' },
    Leda: { gender: 'female', description: 'Youthful and energetic' },
    Orus: { gender: 'male', description: 'Firm and decisive' },
    Puck: { gender: 'male', description: 'Upbeat and energetic' },
    Pulcherrima: { gender: 'female', description: 'Forward and expressive' },
    Rasalgethi: { gender: 'male', description: 'Informative and professional' },
    Sadachbia: { gender: 'male', description: 'Lively and animated' },
    Sadaltager: { gender: 'male', description: 'Knowledgeable and authoritative' },
    Schedar: { gender: 'male', description: 'Even and balanced' },
    Sulafat: { gender: 'female', description: 'Warm and welcoming' },
    Umbriel: { gender: 'male', description: 'Easy-going and calm' },
    Vindemiatrix: { gender: 'female', description: 'Gentle and kind' },
    Zephyr: { gender: 'female', description: 'Bright and cheerful' },
    Zubenelgenubi: { gender: 'male', description: 'Casual and conversational' },
};

/** "Aoede (female)" - how a voice is written in a menu */
export const voiceLabel = (name) => {
    const info = VOICE_INFO[name];
    return info ? `${name} (${info.gender})` : name;
};

/** The names of the voices: the female voices first, then the male voices, each group in alphabetical order */
export const sortVoices = (names) => {
    const rank = (name) => (VOICE_INFO[name]?.gender === 'male' ? 1 : 0);
    return [...names].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
};

/**
 * The languages of the automatic creation. The keys are the ones that are saved in the database.
 * testLanguage - the code that is sent to the server when a sample is created (the models detect the language
 * from the text, the code only decides the accent of English); sample - the word of the sample.
 */
export const LANGUAGE_KEYS = ['he', 'en_US', 'en_GB', 'ar'];

export const LANGUAGE_INFO = {
    he: { label: 'עברית', testLanguage: 'he-IL', sample: 'שלום' },
    en_US: { label: 'אנגלית אמריקאית', testLanguage: 'en-US', sample: 'apple' },
    en_GB: { label: 'אנגלית בריטית', testLanguage: 'en-GB', sample: 'colour' },
    ar: { label: 'ערבית', testLanguage: 'ar-001', sample: 'مرحبا' },
};

/** The languages of the test form: the code that is sent to the server, and how it is shown */
export const TEST_LANGUAGES = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'en-GB', label: 'English (GB)' },
    { code: 'he-IL', label: 'עברית (ישראל)' },
    { code: 'ar-001', label: 'العربية' },
];

// ____________________________________________________________________________________________
// answers of the server

/** The server writes the type of the audio in the Content-Type, for example "data:audio/wav;base64" */
export function getMimeFromContentType(contentType) {
    const match = /^\s*data:([a-z0-9.+-]+\/[a-z0-9.+-]+)/i.exec(String(contentType || ''));
    return match ? match[1].toLowerCase() : 'audio/mp3';
}

export function extensionOfMime(mime) {
    if (/wav/i.test(mime)) return 'wav';
    if (/mp3|mpeg/i.test(mime)) return 'mp3';
    return 'audio';
}

const ENTITIES = { '&quot;': '"', '&#34;': '"', '&#39;': "'", '&#x27;': "'", '&lt;': '<', '&gt;': '>', '&amp;': '&' };

/** The server escapes the text of its errors as HTML ("&quot;") - this writes them as plain text again */
export const decodeHtmlEntities = (text) => String(text).replace(/&(?:quot|#34|#39|#x27|lt|gt|amp);/g, (entity) => ENTITIES[entity]);

/**
 * What to show for a request that failed. The server answers with text, or with { error, errors }.
 * @returns {{ status: number, message: string, detail: string }}
 */
export function describeError(err) {
    const status = err?.response?.status || 0;
    const data = err?.response?.data;
    let detail = '';
    if (typeof data === 'string') {
        detail = decodeHtmlEntities(data.replace(/<[^>]*>/g, ' '));
    } else if (data && typeof data === 'object') {
        const parts = [];
        if (data.error) parts.push(String(data.error));
        else if (data.message) parts.push(String(data.message));
        if (Array.isArray(data.errors)) parts.push(...data.errors.map(String));
        detail = parts.join(' | ');
    }
    detail = detail.replace(/\s+/g, ' ').trim();
    if (detail.length > 500) detail = `${detail.slice(0, 500)}…`;
    return { status, message: status ? `HTTP ${status}` : (err?.message || 'Network error'), detail };
}

/** "HTTP 429: Gemini API quota exceeded..." */
export const formatError = (error) => (error.detail ? `${error.message}: ${error.detail}` : error.message);
